export const BUSINESS_TYPES = [
  { id: 'legal',                    label: 'Legal Firm' },
  { id: 'accounting',               label: 'Accounting Practice' },
  { id: 'real_estate',              label: 'Real Estate Brokerage' },
  { id: 'dental',                   label: 'Dental Clinic' },
  { id: 'medical_clinics',          label: 'Medical Clinics' },
  { id: 'hvac',                     label: 'HVAC Contractor' },
  { id: 'plumber',                  label: 'Plumbing Contractor' },
  { id: 'electrician',              label: 'Electrical Services' },
  { id: 'construction',             label: 'Construction' },
  { id: 'mortgage_broker',          label: 'Mortgage Brokerage' },
  { id: 'immigration_consultant',   label: 'Immigration Consultant' },
  { id: 'law_enforcement_supplier', label: 'Law Enforcement Supplier' },
  { id: 'ecommerce',                label: 'E-Commerce' },
  { id: 'creative_agency',          label: 'Creative Agency' },
  { id: 'it_msps',                  label: 'IT / MSPs' },
  { id: 'non_profits',              label: 'Non-Profits' },
  { id: 'trade_schools',            label: 'Trade Schools / Coaching' },
  { id: 'hr_recruiting',            label: 'HR / Recruiting' },
  { id: 'logistics',                label: 'Logistics' },
  { id: 'hospitality',              label: 'Restaurant & Hospitality' },
  { id: 'insurance',                label: 'Insurance' },
  { id: 'wealth_management',        label: 'Wealth Management' },
  { id: 'labs_diagnostics',         label: 'Labs / Diagnostics' },
  { id: 'print_publishing',         label: 'Print / Publishing' },
  { id: 'small_farms',              label: 'Small Farms' },
  { id: 'other',                    label: 'Other' },
];

export const DEFAULT_BUSINESS_TYPE = 'legal';

const normalize = (value) => String(value || '').trim().toLowerCase();

export const normalizeBusinessType = (value, fallback = '') => {
  const raw = normalize(value);
  if (!raw) return fallback;
  if (BUSINESS_TYPES.some((bt) => bt.id === raw)) return raw;
  const compact = raw.replace(/[^a-z0-9]/g, '');
  const byLabel = BUSINESS_TYPES.find(
    (bt) => bt.label.toLowerCase().replace(/[^a-z0-9]/g, '') === compact,
  );
  if (byLabel) return byLabel.id;
  return fallback;
};

const STACK_ID_TO_BUSINESS_TYPE = {
  'real-estate':               'real_estate',
  'legal-practice':            'legal',
  accounting:                  'accounting',
  healthcare:                  'dental',
  'manufacturing-smb':         'electrician',
  'construction-trades':       'plumber',
  restaurant:                  'plumber',
  ecommerce:                   'mortgage_broker',
  'education-coaching':        'legal',
  'marketing-agency':          'accounting',
  'winter-home-services':      'hvac',
  'saas-startup':              'electrician',
  'public-services-office':    'legal',
  'nonprofit-fundraising-team':'legal',
};

const BUSINESS_TYPE_TO_STACK_ID = {
  legal: 'legal-practice',
  accounting: 'accounting',
  real_estate: 'real-estate',
  dental: 'healthcare',
  medical_clinics: 'healthcare',
  hvac: 'winter-home-services',
  plumber: 'construction-trades',
  electrician: 'construction-trades',
  construction: 'construction-trades',
  mortgage_broker: 'accounting',
  immigration_consultant: 'education-coaching',
  law_enforcement_supplier: 'public-services-office',
  ecommerce: 'ecommerce',
  creative_agency: 'marketing-agency',
  it_msps: 'saas-startup',
  non_profits: 'nonprofit-fundraising-team',
  trade_schools: 'education-coaching',
  hr_recruiting: 'education-coaching',
  logistics: 'construction-trades',
  hospitality: 'restaurant',
  insurance: 'accounting',
  wealth_management: 'accounting',
  labs_diagnostics: 'healthcare',
  print_publishing: 'marketing-agency',
  small_farms: 'manufacturing-smb',
  other: 'legal-practice',
};

export const resolveBusinessTypeFromStackId = (stackId, fallback = '') => {
  const key = normalize(stackId);
  return STACK_ID_TO_BUSINESS_TYPE[key] || fallback;
};

export const resolveStackIdFromBusinessType = (businessType, fallback = '') => {
  const key = normalizeBusinessType(businessType, '');
  return BUSINESS_TYPE_TO_STACK_ID[key] || fallback;
};

export const BUSINESS_TYPE_BENCHMARKS = {
  legal:                    { label: 'Legal Firm',                   baseError: 50000, workflow: 'Filing & Triage' },
  accounting:               { label: 'Accounting Practice',          baseError: 25000, workflow: 'Receipt & Tax Staging' },
  real_estate:              { label: 'Real Estate Brokerage',        baseError: 36000, workflow: 'Lease Escalations' },
  dental:                   { label: 'Dental Clinic',                baseError: 42000, workflow: 'Billing & Coding' },
  medical_clinics:          { label: 'Medical Clinics',              baseError: 42000, workflow: 'Billing & Coding' },
  hvac:                     { label: 'HVAC Contractor',              baseError: 350,   workflow: 'Field Dispatch' },
  plumber:                  { label: 'Plumbing Contractor',          baseError: 45000, workflow: 'Change Orders' },
  electrician:              { label: 'Electrical Services',          baseError: 15000, workflow: 'Material Quotes' },
  construction:             { label: 'Construction',                 baseError: 45000, workflow: 'Change Orders' },
  mortgage_broker:          { label: 'Mortgage Brokerage',           baseError: 2500,  workflow: 'Onboarding' },
  immigration_consultant:   { label: 'Immigration Consultant',       baseError: 4000,  workflow: 'Resume Triage' },
  law_enforcement_supplier: { label: 'Law Enforcement Supplier',     baseError: 12000, workflow: 'Freight Audit' },
  ecommerce:                { label: 'E-Commerce',                   baseError: 30000, workflow: 'Inventory Sync' },
  creative_agency:          { label: 'Creative Agency',              baseError: 28000, workflow: 'Scope Creep' },
  it_msps:                  { label: 'IT / MSPs',                    baseError: 24000, workflow: 'SLA Tracking' },
  non_profits:              { label: 'Non-Profits',                  baseError: 20000, workflow: 'Grant Tracking' },
  trade_schools:            { label: 'Trade Schools / Coaching',     baseError: 1500,  workflow: 'Student Leads' },
  hr_recruiting:            { label: 'HR / Recruiting',              baseError: 4000,  workflow: 'Resume Triage' },
  logistics:                { label: 'Logistics',                    baseError: 12000, workflow: 'Freight Audit' },
  hospitality:              { label: 'Restaurant & Hospitality',     baseError: 18000, workflow: 'Direct Booking' },
  insurance:                { label: 'Insurance',                    baseError: 1200,  workflow: 'Policy Renewals' },
  wealth_management:        { label: 'Wealth Management',            baseError: 2500,  workflow: 'Onboarding' },
  labs_diagnostics:         { label: 'Labs / Diagnostics',           baseError: 500,   workflow: 'Sample Chain' },
  print_publishing:         { label: 'Print / Publishing',           baseError: 2000,  workflow: 'Pre-flight Errors' },
  small_farms:              { label: 'Small Farms',                  baseError: 12000, workflow: 'Yield Variance' },
  other:                    { label: 'Other',                        baseError: 20000, workflow: 'General Intake' },
};

export const deriveContext = ({
  businessType = '',
  selectedStackId = '',
} = {}) => {
  const normalizedStackId = normalize(selectedStackId) || '';
  const normalizedType = normalizeBusinessType(businessType, '')
    || resolveBusinessTypeFromStackId(normalizedStackId, '');
  return {
    businessType: normalizedType,
    selectedStackId: normalizedStackId || resolveStackIdFromBusinessType(normalizedType, ''),
  };
};